import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useGeolocation } from '../hooks/useGeolocation'
import { useHunts } from '../hooks/useHunt'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { BottomNav } from '../components/common/BottomNav'
import HuntCard from '../components/Hunt/HuntCard'

type SortOption = 'distance' | 'newest' | 'popular'

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: 'distance', label: 'Nearest' },
  { value: 'newest', label: 'Newest' },
  { value: 'popular', label: 'Popular' },
]

export default function HuntList() {
  const { location, loading: geoLoading, error: geoError, requestLocation } = useGeolocation()
  const [difficulty, setDifficulty] = useState<string>('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortOption>('distance')
  const [page, setPage] = useState(1)
  const [locationRequested, setLocationRequested] = useState(false)

  // Request location on mount
  useEffect(() => {
    if (!locationRequested) {
      setLocationRequested(true)
      requestLocation()
    }
  }, [locationRequested, requestLocation])

  // Debounce search input by 300ms
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(handle)
  }, [searchInput])

  const { data, isLoading } = useHunts({
    lat: location?.latitude,
    lon: location?.longitude,
    radius: 50000,
    difficulty: difficulty || undefined,
    search: search || undefined,
    sort,
    page,
    per_page: 20,
  })

  return (
    <div className="min-h-screen bg-cream-50 pb-24">
      {/* Header */}
      <header className="bg-white border-b border-cream-200 sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-brown-600 hover:text-brown-800 transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-bold text-brown-900">Explore Hunts</h1>
          </div>

          {/* Search */}
          <div className="mt-4 relative">
            <span className="absolute inset-y-0 left-3 flex items-center text-brown-400 pointer-events-none">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
            </span>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search hunts by name or description..."
              className="w-full pl-10 pr-3 py-2.5 rounded-full bg-cream-100 border border-cream-200 text-sm text-brown-900 placeholder-brown-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:bg-white transition-colors"
            />
          </div>

          {/* Location status */}
          <div className="mt-3 text-sm">
            {geoLoading ? (
              <span className="text-brown-500 flex items-center gap-1.5">
                <svg className="w-4 h-4 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Getting your location...
              </span>
            ) : location ? (
              <span className="text-success-600 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Showing hunts near you
              </span>
            ) : geoError ? (
              <button onClick={requestLocation} className="text-warning-600 hover:text-warning-700 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Location unavailable - tap to retry
              </button>
            ) : (
              <button onClick={requestLocation} className="text-primary-600 hover:text-primary-700 flex items-center gap-1.5">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                </svg>
                Enable location for nearby hunts
              </button>
            )}
          </div>

          {/* Sort segmented control */}
          <div className="flex gap-1 mt-4 p-1 rounded-full bg-cream-100 w-fit">
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setSort(opt.value)
                  setPage(1)
                }}
                className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  sort === opt.value
                    ? 'bg-white text-brown-900 shadow-sm'
                    : 'text-brown-600 hover:text-brown-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Difficulty filters */}
          <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
            {['', 'easy', 'medium', 'hard'].map((d) => (
              <button
                key={d}
                onClick={() => {
                  setDifficulty(d)
                  setPage(1)
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  difficulty === d
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'bg-cream-100 text-brown-700 hover:bg-cream-200'
                }`}
              >
                {d === '' ? 'All' : d.charAt(0).toUpperCase() + d.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {isLoading ? (
          <LoadingSpinner className="py-12" />
        ) : data?.items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-brown-600">
              {search ? `No hunts found for "${search}".` : 'No hunts found.'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {data?.items.map((hunt) => (
                <HuntCard key={hunt.id} hunt={hunt} />
              ))}
            </div>

            {/* Pagination */}
            {data && data.pages > 1 && (
              <div className="flex justify-center gap-2 mt-8">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-brown-600">
                  {page} / {data.pages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={page === data.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </main>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  )
}
